/**
 * Holdings API Hooks
 *
 * React Query hooks for holdings-related API operations.
 */

import { toEasternDate, type Holding, type CreateHoldingDto } from '@luminaria/shared';
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { holdingsApi, type UpdateHoldingInput } from '../api/holdingsApi';
import { invalidate } from '../api/invalidation';
import { holdingKeys } from '../api/queryKeys';

// Re-export types for convenience
export type { Holding } from '@luminaria/shared';

/**
 * Convenience wrapper for fetching holdings by account or portfolio
 */
export function useHoldings(
  id: string,
  scope: 'account' | 'portfolio' = 'portfolio',
  options?: Omit<UseQueryOptions<Holding[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Holding[]>({
    queryKey: scope === 'account' ? holdingKeys.byAccount(id) : holdingKeys.byPortfolio(id),
    queryFn: () => (scope === 'account' ? holdingsApi.listByAccount(id) : holdingsApi.listByPortfolio(id)),
    enabled: !!id,
    ...options,
  });
}

/**
 * Fetch all holdings for a portfolio
 */
export function useHoldingsByPortfolio(
  portfolioId: string,
  options?: Omit<UseQueryOptions<Holding[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Holding[]>({
    queryKey: holdingKeys.byPortfolio(portfolioId),
    queryFn: () => holdingsApi.listByPortfolio(portfolioId),
    enabled: !!portfolioId,
    ...options,
  });
}

type CreateHoldingVariables = Omit<CreateHoldingDto, 'accountId'> & { accountId: string };

/**
 * Create a new holding in an account
 */
export function useCreateHolding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accountId, ...dto }: CreateHoldingVariables) => holdingsApi.create(accountId, dto),
    onSuccess: () => { void invalidate(queryClient, 'holdings'); },
  });
}

type UpdateHoldingVariables = UpdateHoldingInput & { id: string };

/**
 * Update an existing holding
 */
export function useUpdateHolding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...updates }: UpdateHoldingVariables) => holdingsApi.update(id, updates),
    onSuccess: () => { void invalidate(queryClient, 'holdings'); },
  });
}

/**
 * Delete a holding
 */
export function useDeleteHolding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => holdingsApi.remove(id),
    onSuccess: () => { void invalidate(queryClient, 'holdings'); },
  });
}

/**
 * Convert a stale synced holding to a manual holding
 * Only stale holdings (missing from latest brokerage sync) can be converted.
 */
export function useConvertHoldingToManual() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => holdingsApi.convertToManual(id),
    onSuccess: () => { void invalidate(queryClient, 'holdings'); },
  });
}

const CLEANUP_STORAGE_KEY = 'lastExpiredOptionsCleanup';

/**
 * Get today's date string in YYYY-MM-DD format (ET timezone)
 */
function getTodayET(): string {
  return toEasternDate(new Date());
}

/**
 * Check if cleanup has already run today
 */
function hasCleanupRunToday(): boolean {
  const lastCleanup = localStorage.getItem(CLEANUP_STORAGE_KEY);
  return lastCleanup === getTodayET();
}

/**
 * Mark cleanup as run for today
 */
function markCleanupRun(): void {
  localStorage.setItem(CLEANUP_STORAGE_KEY, getTodayET());
}

/**
 * Cleanup expired options from holdings (runs once per day)
 * 
 * This hook automatically cleans up option holdings that have passed their
 * removal time (9:30 AM ET the day after expiration). It only runs once per day,
 * tracked via localStorage.
 */
export function useCleanupExpiredOptions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Skip if already run today
      if (hasCleanupRunToday()) {
        return { deletedCount: 0, deletedHoldings: [], skipped: true };
      }

      const data = await holdingsApi.cleanupExpiredOptions();

      // Mark as run for today
      markCleanupRun();
      
      return { ...data, skipped: false };
    },
    onSuccess: (result) => {
      if (result.deletedCount > 0) {
        void invalidate(queryClient, 'holdings');
      }
    },
  });
}
